// "Why I'm attending" — renders the server-evaluated reason JSON as one plain
// sentence. Audience rules are NEVER re-evaluated here; we only echo the labels
// the backend matched ("Required because your department is Finance"). Phase 2
// rules add an `op` per matched condition ('in', 'contains', 'subtree_of',
// 'within_days', … or 'not <op>' when a NOT branch matched) and a `rule_name`.
import { useT } from '../lib/i18n';
import { optionLabel } from '../lib/audienceRules';
import React from 'react';
import type { AssignmentReason, AssignmentType, MatchedCondition } from '@/services/learningService';

type T = (k: string, d: string, o?: any) => string;

/** For id-valued facets that may be absent (branch, job title): whether the match says the person
 *  HAS one, has NONE, or names a specific value (null). `not_in` with an empty value is the server's
 *  "not set" match; a NOT branch over is_set / is_not_set flips. */
function presence(op: string, negated: boolean, value: string): 'has' | 'none' | null {
  if (op === 'is_set' || op === 'is_not_set') return (op === 'is_set') !== negated ? 'has' : 'none';
  if (op === 'not_in' && !negated && !value) return 'none';
  return null;
}

/** Role-type matches: the server labels a `not_in` hit as "not a/b" — split that into a negated phrase over localised keys. */
function roleValue(t: T, field: 'role' | 'custom_role', op: string, negated: boolean, label: string): { neg: boolean; value: string } {
  let neg = negated;
  let raw = label;
  if (!neg && op === 'not_in' && /^not\s+/i.test(raw)) { neg = true; raw = raw.replace(/^not\s+/i, ''); }
  const keys = raw.split('/').map((k) => k.trim()).filter(Boolean);
  const value = keys.map((k) => (field === 'role' ? optionLabel(t, 'role', k) : k)).join(' / ');
  return { neg, value: value || raw };
}

/** One matched condition → "your department is Finance" (or its negation). */
export function conditionPhrase(t: T, c: MatchedCondition): string {
  const label = c.label || c.value;
  const rawOp = (c.op || '').trim();
  const negated = rawOp.startsWith('not ');
  const op = negated ? rawOp.slice(4).trim() : rawOp;
  const field = c.field === 'department' ? 'department_id' : c.field === 'branch' ? 'branch_id' : c.field === 'job_title' ? 'job_title_id' : c.field;
  switch (field) {
    case 'branch_id': {
      const has = presence(op, negated, c.value);
      if (has === 'has') return t('training:reason_branch_set', 'you have a branch');
      if (has === 'none') return t('training:reason_branch_not_set', 'you have no branch');
      return negated ? t('training:reason_branch_not', 'your branch is not {{value}}', { value: label }) : t('training:reason_branch', 'your branch is {{value}}', { value: label });
    }
    case 'job_title_id': {
      const has = presence(op, negated, c.value);
      if (has === 'has') return t('training:reason_job_title_set', 'you have a job title');
      if (has === 'none') return t('training:reason_job_title_not_set', 'you have no job title');
      return negated ? t('training:reason_job_title_not', 'your job title is not {{value}}', { value: label }) : t('training:reason_job_title', 'your job title is {{value}}', { value: label });
    }
    case 'work_location_type': {
      const value = optionLabel(t, 'work_location_type', label);
      return negated ? t('training:reason_work_location_not', 'your work location type is not {{value}}', { value }) : t('training:reason_work_location', 'your work location type is {{value}}', { value });
    }
    case 'role': {
      const { neg, value } = roleValue(t, 'role', op, negated, label);
      return neg ? t('training:reason_role_not', 'your user type is not {{value}}', { value }) : t('training:reason_role', 'your user type is {{value}}', { value });
    }
    case 'custom_role': {
      const { neg, value } = roleValue(t, 'custom_role', op, negated, label);
      return neg ? t('training:reason_custom_role_not', 'your custom role is not {{value}}', { value }) : t('training:reason_custom_role', 'your custom role is {{value}}', { value });
    }
    case 'department_id':
      return negated ? t('training:reason_department_not', 'your department is not {{value}}', { value: label }) : t('training:reason_department', 'your department is {{value}}', { value: label });
    case 'employment_type':
      return negated ? t('training:reason_employment_type_not', 'your employment type is not {{value}}', { value: label }) : t('training:reason_employment_type', 'your employment type is {{value}}', { value: label });
    case 'position':
      if (op === 'contains') return negated ? t('training:reason_position_not_contains', 'your job title does not contain {{value}}', { value: label }) : t('training:reason_position_contains', 'your job title contains {{value}}', { value: label });
      return negated ? t('training:reason_position_not', 'your job title is not {{value}}', { value: label }) : t('training:reason_position', 'your job title is {{value}}', { value: label });
    case 'org_level': case 'career_level':
      return negated ? t('training:reason_level_not', 'your level is not {{value}}', { value: label }) : t('training:reason_level', 'your level is {{value}}', { value: label });
    case 'manager':
      return negated ? t('training:reason_manager_not', 'you are not in the reporting line "{{value}}"', { value: label }) : t('training:reason_manager', 'you {{value}}', { value: label });
    case 'hire_date':
      return negated ? t('training:reason_hire_date_not', 'you were not {{value}}', { value: label }) : t('training:reason_hire_date', 'you were {{value}}', { value: label });
    case 'status':
      return negated ? t('training:reason_status_not', 'your status is not {{value}}', { value: label }) : t('training:reason_status', 'your status is {{value}}', { value: label });
    case 'employee':
      return negated ? t('training:reason_employee_not', 'you were not selected by name') : t('training:reason_employee', 'you were selected by name');
    default:
      return negated ? t('training:reason_condition_not', 'your {{field}} is not {{value}}', { field: c.field, value: label }) : t('training:reason_condition', 'your {{field}} is {{value}}', { field: c.field, value: label });
  }
}

export function reasonCopy(t: T, type?: AssignmentType | null, reason?: AssignmentReason | null): string | null {
  const typeLabel = {
    required: t('training:required', 'Required'),
    recommended: t('training:recommended', 'Recommended'),
    optional: t('training:optional', 'Optional'),
  }[type || 'optional'] || t('training:optional', 'Optional');
  if (!reason) return null;
  if (reason.type === 'MANUAL_INCLUDE') {
    return t('training:reason_manual', 'Added by your manager/HR');
  }
  // Presence ops (is_set / is_not_set, possibly under NOT) carry no value or label yet still read as a sentence.
  const conds = (reason.matched_conditions || []).filter((c) => c && (c.label || c.value || c.field === 'employee' || /\bis_(not_)?set$/.test(c.op || '')));
  if (conds.length === 0) return t('training:reason_rule_generic', '{{type}} for you by an audience rule', { type: typeLabel });
  const parts = Array.from(new Set(conds.map((c) => conditionPhrase(t, c))));
  return t('training:reason_rule', '{{type}} because {{conditions}}', { type: typeLabel, conditions: parts.join(t('training:reason_and', ' and ')) });
}

/** Short labels for a "Matched: …" tooltip — the plain labels the server matched, one per condition. */
export function matchedLabels(reason?: AssignmentReason | null): string[] {
  if (!reason || reason.type !== 'AUDIENCE_RULE') return [];
  return (reason.matched_conditions || []).map((c) => c.label || c.value).filter(Boolean);
}

export const ReasonLine: React.FC<{ type?: AssignmentType | null; reason?: AssignmentReason | null; className?: string }> = ({ type, reason, className }) => {
  const t = useT();
  const copy = reasonCopy(t, type, reason);
  if (!copy) return null;
  const ruleName = reason?.type === 'AUDIENCE_RULE' ? reason.rule_name : null;
  return (
    <p data-testid="reason-line" className={`text-xs text-muted-foreground ${className || ''}`}>
      {copy}
      {ruleName ? <span className="opacity-70" data-testid="reason-rule-name"> · {ruleName}</span> : null}
    </p>
  );
};

export default ReasonLine;
